import { Cancel, Delete } from '@mui/icons-material';
import {
    styled,
    Dialog,
    Typography,
    Button,
    DialogContent,
} from '@mui/material';
import { ComponentProps, MouseEvent } from 'react';
import { LoadingButton } from './buttons/LoadingButton';

export interface PropTypes extends ComponentProps<typeof Dialog> {
    title: string,
    description?: string,
    onDelete: (event: MouseEvent<HTMLButtonElement>) => Promise<void> | void,
    onCancel: (event: MouseEvent<HTMLButtonElement>) => void,
}

const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiPaper-root': {
        padding: theme.spacing(8),
    },
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing(4),
    padding: 0,
    '&:first-of-type': {
        paddingTop: 0,
    },
}));

const Buttons = styled('div')(({ theme }) => ({
    display: 'grid',
    paddingTop: theme.spacing(4),
    gap: theme.spacing(2),
    gridTemplateAreas: '". ."',
    justifyContent: 'center',
}));

const DeleteButton = styled(LoadingButton)(({ theme }) => ({
    color: theme.palette.common.white,
    backgroundColor: theme.palette.error.main,
    '&:hover': {
        backgroundColor: theme.palette.error.dark,
    },
}));

export const DeleteConfirmationDialog = (props: PropTypes): JSX.Element => {
    const {
        open,
        title,
        description,
        children,
        onDelete,
        onCancel,
        ...rest
    } = props;

    return (
        <StyledDialog
            {...rest}
            maxWidth="xs"
            open={open}
            onClose={onCancel}
        >
            <StyledDialogContent>
                <Typography
                    textAlign="center"
                    variant="h6"
                >
                    {title}
                </Typography>
                <Typography
                    textAlign="center"
                    variant="subtitle1"
                >
                    {description}
                </Typography>
                <Buttons>
                    <Button
                        color="secondary"
                        startIcon={<Cancel />}
                        variant="outlined"
                        onClick={onCancel}
                    >
                        Cancel
                    </Button>
                    <DeleteButton
                        color="error"
                        Icon={Delete}
                        startIcon={<Delete />}
                        variant="contained"
                        onClick={onDelete}
                    >
                        Delete
                    </DeleteButton>
                </Buttons>
                {children}
            </StyledDialogContent>
        </StyledDialog>
    );
};
